import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OtpInput from '../components/common/OtpInput';
import DatePicker from '../components/common/DatePicker';
import AuthShell from '../components/layout/AuthShell';
import { useRequestOtpMutation, useSignupMutation } from '../store/authApi';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import {
  UI, hL, lead, input, fieldLabel, btnYellow, btnLg, textbtn, lnk, spin,
  alertBad, num,
} from '../styles/tl5';

// Patient self-signup form. user_type is fixed to "patient" — no UI
// for it. Other roles (doctor, lab partner, organisation) are
// onboarded internally by the admin team and never come through this
// page.
//
// Two-step flow:
//   1. Details — Full Name, Mobile, DOB, Gender. On submit we call
//      /auth/request-otp to ship a 6-digit code to the phone.
//   2. OTP — user enters the code; we call /auth/signup with the
//      full payload + otp. Server re-verifies the OTP (defence in
//      depth) and creates the account.
const GENDER_OPTIONS = [
  { value: 'male',   label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other',  label: 'Other' },
];

const Signup = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { showToast } = useUI();

  const [step, setStep] = useState('details'); // 'details' | 'otp'
  const [form, setForm] = useState({
    name: '',
    phone: '',
    dob: '',
    gender: '',
  });
  const [otp, setOtp] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const [requestOtp, { isLoading: sending }] = useRequestOtpMutation();
  const [signup,     { isLoading: creating }] = useSignupMutation();

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Phone is 10 digits in state; prepend +91 only when calling the API.
  const fullPhone = `+91${form.phone}`;

  // ── Step 1: validate + request OTP ──
  const onSendOtp = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!form.name.trim())             return setErrMsg('Please enter your full name');
    if (!/^\d{10}$/.test(form.phone))  return setErrMsg('Enter a valid 10-digit mobile number');
    if (!form.dob)                     return setErrMsg('Please enter your date of birth');
    if (!form.gender)                  return setErrMsg('Please select your gender');

    try {
      await requestOtp({ phone: fullPhone }).unwrap();
      setStep('otp');
      showToast('OTP sent — check your phone');
    } catch (err) {
      setErrMsg(err?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  // ── Step 2: submit signup with OTP ──
  const onCreateAccount = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!/^\d{6}$/.test(otp.trim())) return setErrMsg('Enter the 6-digit OTP');

    try {
      const res = await signup({
        name: form.name.trim(),
        phone: fullPhone,
        otp: otp.trim(),
        dob: form.dob,
        gender: form.gender,
        user_type: 'patient',
      }).unwrap();

      // Signup is OTP-based, not credentials-based — leave the
      // /reports UHID gate enabled until the user goes through
      // verify-credentials at least once.
      setSession({ token: res.token, user: res.user, uhidVerified: false });
      showToast(`Welcome to TrustLab, ${res.user.name}!`);
      navigate(ROUTES.HOME, { replace: true });
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not create account. Please try again.');
    }
  };

  return (
    <AuthShell>
      <Typography component="h1" sx={hL}>
        {step === 'details' ? 'Create your account' : 'Verify your phone'}
      </Typography>
      <Typography sx={{ ...lead, mt: '8px', mb: '26px' }}>
        {step === 'details'
          ? 'A one-time password will verify your mobile number.'
          : `Enter the 6-digit code we sent to +91 ${form.phone}.`}
      </Typography>

      {errMsg && <Box sx={{ ...alertBad, mb: '20px' }} role="alert">{errMsg}</Box>}

      {step === 'details' && (
        <Box component="form" onSubmit={onSendOtp} sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Box>
            <Box component="label" htmlFor="su-name" sx={fieldLabel}>Full name</Box>
            <Box
              component="input"
              id="su-name"
              value={form.name}
              onChange={onChange('name')}
              autoComplete="name"
              autoFocus
              disabled={sending}
              sx={input}
            />
          </Box>

          <Box>
            <Box component="label" htmlFor="su-phone" sx={fieldLabel}>Mobile number</Box>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="span"
                sx={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  pr: '10px', borderRight: '1px solid var(--tl-line)',
                  fontFamily: UI, fontSize: 15, fontWeight: 700, color: 'var(--tl-g9)', pointerEvents: 'none',
                }}
              >
                +91
              </Box>
              <Box
                component="input"
                id="su-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="98765 43210"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
                disabled={sending}
                sx={{ ...input, ...num, pl: '72px' }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '18px' }}>
            <Box>
              <Box component="label" sx={fieldLabel}>Date of birth</Box>
              <DatePicker
                value={form.dob}
                onChange={(iso) => setForm((f) => ({ ...f, dob: iso }))}
                disabled={sending}
              />
            </Box>

            <Box>
              <Box component="label" htmlFor="su-gender" sx={fieldLabel}>Gender</Box>
              <Box
                component="select"
                id="su-gender"
                value={form.gender}
                onChange={onChange('gender')}
                disabled={sending}
                sx={{ ...input, cursor: 'pointer' }}
              >
                <option value="">— Select —</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </Box>
            </Box>
          </Box>

          <Box component="button" type="submit" disabled={sending} sx={{ ...btnYellow, width: '100%', minHeight: 58, mt: '4px' }}>
            {sending && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
            {sending ? 'Sending…' : 'Send OTP'}
          </Box>
        </Box>
      )}

      {step === 'otp' && (
        <Box component="form" onSubmit={onCreateAccount} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <OtpInput value={otp} onChange={setOtp} disabled={creating} autoFocus />

          <Box component="button" type="submit" disabled={creating} sx={{ ...btnLg, width: '100%' }}>
            {creating && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
            {creating ? 'Creating…' : 'Verify & create account'}
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => { setStep('details'); setOtp(''); setErrMsg(''); }}
            sx={{ ...textbtn, alignSelf: 'center' }}
          >
            Edit details
          </Box>
        </Box>
      )}

      <Typography sx={{ fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)', textAlign: 'center', mt: '26px' }}>
        Already have an account?{' '}
        <Box component={Link} to={ROUTES.LOGIN} sx={{ ...lnk, display: 'inline', minHeight: 0, fontSize: 14.5 }}>
          Sign in
        </Box>
      </Typography>
    </AuthShell>
  );
};

export default Signup;
